import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { MenuBar } from '@/components/common/MenuBar';
import { News } from '@/components/hotel/News';
import { FooterItems } from '@/components/common/FooterItems';
import { HotelALLReservationTable } from '@/components/hotel/HotelALLReservationTable';
import { getPropertiesAll } from '@/API/apiService';
import Picture from '@/image/hotel/picture9.png';

interface ImageData {
  src: string;
  alt: string;
  text: string;
}

interface ReservationSectionProps {
  areaName?: string;
  propertyId: string;
  studioType: string;
  infoText: string;
  images: ImageData[];
  roomDescription: string;
  stationDistance: string;
  propertyChillenId: string;
  roomChillenId: string;
  monthlyRate?: string;
  dailyRate?: string;
}

const ReservationSection: React.FC<ReservationSectionProps> = ({
  areaName,
  propertyId,
  propertyChillenId,
  roomChillenId,
  studioType,
  images,
  roomDescription,
  stationDistance,
}) => (
  <>
    {areaName && (
      <div className="max-md:py-5 text-center md:text-lg md:mb-6">
        {areaName}
      </div>
    )}
    <div className="mb-4 md:mb-[6.25rem] border-t border-b border-black md:px-4">
      <HotelALLReservationTable
        propertyId={propertyId}
        isShow={false}
        propertyChillenId={propertyChillenId}
        roomChillenId={roomChillenId}
        studioType={studioType}
        images={images}
        roomDescription={roomDescription}
        station_distance={stationDistance}
      />
    </div>
  </>
);

export const HotelPropertiesIndex: React.FC = () => {
  const [reservationSectionsData, setReservationSectionsData] = useState<
    ReservationSectionProps[]
  >([]);
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  // Meta / OGP の内容
  const metaTitle = useEnglish
    ? 'Tokyo Apartments for Rent List | Our Properties'
    : '物件一覧';
  const metaDescription = useEnglish
    ? 'We have a good properties from Studio to 1LDK apartments available that can be rented from one month that are good neighboerhood place.'
    : '1Room～１LDKの部屋をご用意。\n渋谷、代官山、代々木、恵比寿エリア等の物件あり。';

  const ogpTitle = useEnglish
    ? 'The Apartment Hotel by stylio（Official）｜ Tokyo Local Stay'
    : 'The Apartment Hotel by stylio｜東京のネイバーフッドを感じるアパートメントホテル';
  const ogpDescription = useEnglish
    ? "We propose a hotel stay woven into Tokyo's neigborhoods.\nThe apartment hotel brand combines easy accessibility from central Tokyo, while offering a local stay in the cool neighboorhoods."
    : 'ネイバーフッドが豊かな旅を。\n都心へのアクセス性と魅力的な生活圏を兼ねそろえるアパートメントホテル。';
  // OGP 画像は、ここでは Picture を利用（必要に応じて適切な画像URLに変更）
  const ogpImage = Picture;

  useEffect(() => {
    getPropertiesAll()
      .then((response) => {
        const apiData = response.data.data; // APIから取得したデータ配列
        const mappedData: ReservationSectionProps[] = apiData.map(
          (property: any) => {
            return {
              areaName: property.area?.name
                ? `(${useEnglish ? property.area.name_en : property.area.name})`
                : undefined,
              propertyId: property.property_id.toString(),
              propertyChillenId: property.chillnn_id,
              studioType: useEnglish
                ? property.property_name_en
                : property.property_name,
              infoText: useEnglish
                ? property.property_description_en
                : property.property_description,
              stationDistance: useEnglish
                ? property.station_distance_en
                : property.station_distance,
              monthlyRate: property.room_type_lowest_price
                ? parseInt(property.room_type_lowest_price, 10).toLocaleString()
                : '-',
              dailyRate: '-',
              images:
                property.images && property.images.length > 0
                  ? property.images.map((img: any) => ({
                      src: img.image_url,
                      alt: useEnglish ? img.caption_en : img.caption,
                      text: useEnglish ? img.caption_en : img.caption,
                    }))
                  : [
                      {
                        src: Picture,
                        alt: useEnglish ? 'Image 1' : '画像1',
                        text: useEnglish
                          ? 'Fallback image'
                          : 'フォールバック画像',
                      },
                    ],
              roomDescription: useEnglish
                ? property.property_description_en
                : property.property_description,
              roomChillenId: property.chillnn_id,
            };
          }
        );
        setReservationSectionsData(mappedData);
      })
      .catch((err) => {
        console.error('Failed to fetch properties data:', err);
      });
  }, [useEnglish]);

  return (
    <div className="bg-whiteYellow">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={ogpTitle} />
        <meta property="og:description" content={ogpDescription} />
        <meta property="og:image" content={ogpImage} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>
      <MenuBar leftText="Monthly Plan" leftLink={`/monthly/`}>
        <div className="pl-2 md:pl-5 bg-whiteYellow relative w-full">
          <div className="text-lg md:text-2xl px-5 md:pb-10 md:pt-24 max-md:pt-20 max-md:pb-12">
            All Locations
          </div>

          {reservationSectionsData.map((sectionData, index) => (
            <ReservationSection key={index} {...sectionData} />
          ))}

          <div className="px-4">
            <div className="md:px-15 px-4">
              <div className="mb-[6.25rem]">
                <News />
              </div>
            </div>
            <FooterItems />
          </div>
        </div>
      </MenuBar>
    </div>
  );
};

export default HotelPropertiesIndex;
